import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import UploadPage from "@/pages/UploadPage";
import JobsListPage from "@/pages/JobsListPage";
import JobDetailPage from "@/pages/JobDetailPage";
import ReviewPage from "@/pages/ReviewPage";
import PortalPage from "@/pages/PortalPage";
import TemplatesPage from "@/pages/TemplatesPage";
import DigitizeLandingPage from "@/pages/DigitizeLandingPage";
import DigitizeUploadPage from "@/pages/DigitizeUploadPage";
import DigitizeDashboardPage from "@/pages/DigitizeDashboardPage";
import CloseoutDashboard from "@/pages/closeout/CloseoutDashboard";
import CloseoutNewProject from "@/pages/closeout/CloseoutNewProject";
import CloseoutProject from "@/pages/closeout/CloseoutProject";
import ProcessMapPage from "@/pages/ProcessMapPage";
import OnePagerPage from "@/pages/OnePagerPage";
import DemoPage from "@/pages/DemoPage";
import VideoSharePage from "@/pages/VideoSharePage";
import UserManagementPage from "@/pages/UserManagementPage";
import SettingsPage from "@/pages/SettingsPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import RfiPublicPage from "@/pages/closeout/RfiPublicPage";
import TradePortal from "@/pages/closeout/TradePortal";
import HandoverPortal from "@/pages/HandoverPortal";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function FullScreenRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  return children;
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
            <Route path="/digitize" element={<PublicRoute><DigitizeLandingPage /></PublicRoute>} />
            <Route path="/demo" element={<PublicRoute><DemoPage /></PublicRoute>} />
            <Route path="/demo/video/:slug" element={<PublicRoute><VideoSharePage /></PublicRoute>} />
            <Route path="/digitize/upload" element={<ProtectedRoute><DigitizeUploadPage /></ProtectedRoute>} />
            <Route path="/digitize/projects" element={<ProtectedRoute><DigitizeDashboardPage /></ProtectedRoute>} />
            <Route path="/closeout" element={<ProtectedRoute><CloseoutDashboard /></ProtectedRoute>} />
            <Route path="/closeout/new" element={<ProtectedRoute><CloseoutNewProject /></ProtectedRoute>} />
            <Route path="/closeout/:projectId" element={<ProtectedRoute><CloseoutProject /></ProtectedRoute>} />
            <Route path="/process-map" element={<ProtectedRoute><ProcessMapPage /></ProtectedRoute>} />
            <Route path="/about" element={<ProtectedRoute><OnePagerPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
            <Route path="/jobs" element={<ProtectedRoute><JobsListPage /></ProtectedRoute>} />
            <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
            <Route path="/jobs/:jobId/review" element={<FullScreenRoute><ReviewPage /></FullScreenRoute>} />
            <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
            <Route path="/portal/:shareToken" element={<PortalPage />} />
            <Route path="/rfi/:rfiId" element={<RfiPublicPage />} />
            <Route path="/trade-portal/:token" element={<TradePortal />} />
            <Route path="/handover/:token" element={<HandoverPortal />} />
            <Route path="/admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/space" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
            <Route path="/recover" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
            <Route path="/predict" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
            <Route path="/operate" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
            <Route path="/assess" element={<ProtectedRoute><ComingSoonPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
